import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// GlassCard – translucent stat card for dark/hero contexts
// ---------------------------------------------------------------------------

/**
 * Color accent options for the glass card
 */
export type GlassCardAccent = 'white' | 'emerald' | 'violet' | 'amber' | 'teal' | 'blue' | 'red';

interface GlassCardSignature {
  Args: {
    /** Phosphor icon component to display */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Label text displayed above the value */
    label: string;
    /** Main value to display */
    value: string | number;
    /** Secondary description below the value */
    subtext?: string;
    /** Accent color for value text (default: 'white') */
    accent?: GlassCardAccent;
  };
  Blocks: {
    /** Optional rich subtext content (alternative to @subtext arg, for icons/trends) */
    subtext?: [];
    /** Optional extra content below the value area */
    extra?: [];
  };
  Element: HTMLDivElement;
}

const accentClasses: Record<GlassCardAccent, string> = {
  white: 'text-white',
  emerald: 'text-emerald-300',
  violet: 'text-violet-300',
  amber: 'text-amber-300',
  teal: 'text-teal-300',
  blue: 'text-blue-300',
  red: 'text-red-300',
};

const getAccentClass = (accent: GlassCardAccent = 'white') => accentClasses[accent];

/**
 * GlassCard - A translucent stat card for dark/hero background contexts
 *
 * Designed to sit on gradient backgrounds (e.g., inside a PageHero).
 * Uses frosted glass effect (white/10 bg + backdrop-blur) with white text.
 *
 * @example Basic usage
 * ```gts
 * import GlassCard from 'tio-reimagined/components/glass-card';
 *
 * <GlassCard @label="Career Score" @value="720" @subtext="of 1,000 points" />
 * ```
 *
 * @example Inside a PageHero with accent color
 * ```gts
 * <PageHero @theme="emerald">
 *   <:default>
 *     <div class="grid grid-cols-3 gap-4 mt-6">
 *       <GlassCard @label="Balance" @value="$47,850" @subtext="+12.3% YTD" />
 *       <GlassCard @label="Match" @value="$3,600" @accent="emerald" @subtext="Free money" />
 *       <GlassCard @label="Projected" @value="$1.2M" @subtext="At current rate" />
 *     </div>
 *   </:default>
 * </PageHero>
 * ```
 *
 * @example With rich subtext block (trend icons)
 * ```gts
 * <GlassCard @label="Student Loans" @value="$42,000">
 *   <:subtext>
 *     <PhTrendDown @size={{14}} class="text-emerald-300" />
 *     <span>5.2 years to payoff</span>
 *   </:subtext>
 * </GlassCard>
 * ```
 */
const GlassCard: TOC<GlassCardSignature> = <template>
  <div data-test-glass-card class="bg-white/10 backdrop-blur-sm rounded-xl p-4" ...attributes>
    {{#if @icon}}
      <div class="flex items-center gap-2 mb-2">
        {{#let @icon as |Icon|}}
          <Icon @size={{18}} class="text-white/70" />
        {{/let}}
        <span data-test-glass-card-label class="text-sm text-white/70">{{@label}}</span>
      </div>
    {{else}}
      <div data-test-glass-card-label class="text-sm text-white/70 mb-1">{{@label}}</div>
    {{/if}}
    <div data-test-glass-card-value class="text-2xl font-bold {{getAccentClass @accent}}">
      {{@value}}
    </div>
    {{#if (has-block "subtext")}}
      <div data-test-glass-card-subtext class="text-sm text-white/60 mt-1 flex items-center gap-1">
        {{yield to="subtext"}}
      </div>
    {{else if @subtext}}
      <div data-test-glass-card-subtext class="text-sm text-white/60 mt-1">{{@subtext}}</div>
    {{/if}}
    {{#if (has-block "extra")}}
      <div class="mt-2">
        {{yield to="extra"}}
      </div>
    {{/if}}
  </div>
</template>;

export default GlassCard;

export type { GlassCardSignature };
